import { ref } from 'vue'
import api from '@/api'

const baseRoutes = [
  'Home',
  'UserProfile',
  'Diligence',
  'DiligenceKanban',
  'Entity',
  'Documents',
  'DocumentAnalysis',
  'DiligenceSight',
  'DiligenceReport',
  'DiligenceCustomReport',
  'EntityDocs',
  'RegistrationAnalysis',
  'LawSuits'
]

const admClientRoutes = [
  'Settings',
  'Tracks',
  'EditTrack',
  'ReviewTrack',
  'NewTrack',
  'Reports',
  'FinancialStatement'
]

const admRoutes = ['Users', 'Companies', 'DocumentTypes', 'Terms']

const rolePermissions = {
  QUILL_ADMIN: [...baseRoutes, ...admClientRoutes, ...admRoutes],
  QUILL_DEFAULT: [...baseRoutes, ...admClientRoutes, ...admRoutes],
  ADMIN: [...baseRoutes, ...admClientRoutes],
  DEFAULT: [...baseRoutes]
}

interface Company {
  _id: string
  name: string
  cnpj: string
}

export interface IUser {
  _id: string
  name: string
  email: string
  cpf: string
  avatar: string
  role: string
  company: Company
  createdAt: string
  AccessToken: string
  RefreshToken: string
}

const getNewToken = async (refreshToken: string) => {
  return api.get('/users/auth', {
    headers: {
      RefreshToken: refreshToken
    }
  })
}

export function useAuth() {
  const user = ref<IUser>({
    _id: '',
    name: '',
    email: '',
    cpf: '',
    avatar: '',
    company: { _id: '', name: '', cnpj: '' },
    role: 'DEFAULT',
    createdAt: '',
    AccessToken: '',
    RefreshToken: ''
  })

  const authKey = localStorage.getItem('auth')
  if (authKey) {
    user.value = JSON.parse(authKey)
  }

  const hasPermission = (resource: string) => {
    //@ts-ignore
    return rolePermissions[user.value.role]?.includes(resource)
  }

  const isJwtExpired = async (): Promise<boolean> => {
    const token = user.value?.AccessToken
    if (!token) return true

    try {
      const [, base64Url] = token.split('.')
      if (!base64Url) throw new Error('Invalid JWT token format')

      const jsonPayload = JSON.parse(atob(base64Url))
      const exp = jsonPayload.exp

      if (!exp) throw new Error("No 'exp' field in token")

      if (Math.floor(Date.now() / 1000) < exp) return false

      const refreshToken = user.value?.RefreshToken
      if (!refreshToken) return true

      const response = await getNewToken(refreshToken)
      if (response.status !== 200) throw new Error('Failed to refresh token')

      if (!authKey) return true
      const newSession = {
        ...JSON.parse(authKey),
        AccessToken: response.data.AccessToken
      }
      localStorage.setItem('auth', JSON.stringify(newSession))
      user.value = newSession

      return false
    } catch (error) {
      console.error('Error decoding token:', error)
      return true
    }
  }

  return { user, hasPermission, isJwtExpired }
}
